import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, StatusBadge, PriorityBadge, fmtDate, EmptyState } from "@/components/common";
import { useAuth } from "@/store/auth";
import { toast } from "sonner";
import { Plus, Eye, Search, X, Loader2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SearchableSelect } from "@/components/ui/searchable-select";

const TABS = [
  { key: "", label: "All" },
  { key: "today", label: "Today" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "delayed", label: "Delayed" },
  { key: "awaiting_approval", label: "Awaiting Approval" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "rework", label: "Rework" },
  { key: "return_pending", label: "Return Pending" },
  { key: "closed", label: "Closed" },
  { key: "cancelled", label: "Cancelled" },
];

export default function Tasks() {
  const { user, hasPerm } = useAuth();
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // filter dropdown data
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterPriorities, setFilterPriorities] = useState([]);
  const [filterTaskTypes, setFilterTaskTypes] = useState([]);

  // column sort (client-side on current page)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    api.get("/users?limit=200").then(r => setFilterUsers(r.data?.data || []));
    api.get("/masters/priorities").then(r => setFilterPriorities(r.data?.data || []));
    api.get("/masters/task-types").then(r => setFilterTaskTypes(r.data?.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (tab) params.tab = tab;
      if (search) params.search = search;
      if (assigneeFilter) params.assigned_to = assigneeFilter;
      if (priorityFilter) params.priority_id = priorityFilter;
      if (taskTypeFilter) params.task_type_id = taskTypeFilter;
      const r = await api.get("/tasks", { params });
      setRows(r.data?.data || []);
      setMeta(r.data?.meta || {});
    } finally {
      setLoading(false);
    }
  }, [tab, search, assigneeFilter, priorityFilter, taskTypeFilter, page]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const displayed = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      const cmp = String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const SortTh = ({ label, k, className = "" }) => (
    <th onClick={() => toggleSort(k)} className={`cursor-pointer select-none group ${className}`}>
      <div className="flex items-center gap-1.5">
        {label}
        {sortKey === k
          ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]" /> : <ChevronDown size={11} className="text-[#334E7B]" />)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity" />}
      </div>
    </th>
  );

  const hasFilters = search || assigneeFilter || priorityFilter || taskTypeFilter;
  const clearFilters = () => { setSearch(""); setAssigneeFilter(""); setPriorityFilter(""); setTaskTypeFilter(""); setPage(1); };

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title="Tasks"
        breadcrumbs={["Transactions", "Tasks"]}
        actions={
          hasPerm("create_task") ? (
            <button onClick={() => setCreateOpen(true)} className="erp-btn-primary inline-flex items-center gap-2" data-testid="new-task-btn">
              <Plus size={14} /> New Task
            </button>
          ) : null
        }
      />

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex space-x-6 text-sm font-medium">
          {TABS.map(t => (
            <button
              key={t.key || "all"}
              onClick={() => { setTab(t.key); setPage(1); }}
              className={`pb-2 whitespace-nowrap transition-colors ${tab === t.key ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`tab-${t.key || "all"}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="erp-card p-3 mb-4 flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative min-w-[200px] flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search title or task code..."
            className="erp-input pl-8"
            data-testid="task-search"
          />
          {search && <button onClick={() => { setSearch(""); setPage(1); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>

        {/* Assignee */}
        <div className="min-w-[160px]">
          <SearchableSelect
            options={filterUsers.map(u => ({ value: u.id, label: u.name }))}
            value={assigneeFilter}
            onValueChange={v => { setAssigneeFilter(v); setPage(1); }}
            placeholder="All Assignees"
            allowClear
          />
        </div>

        {/* Priority */}
        <div className="min-w-[140px]">
          <SearchableSelect
            options={filterPriorities.map(p => ({ value: p.id, label: p.name }))}
            value={priorityFilter}
            onValueChange={v => { setPriorityFilter(v); setPage(1); }}
            placeholder="All Priorities"
            allowClear
          />
        </div>

        {/* Task Type */}
        <div className="min-w-[150px]">
          <SearchableSelect
            options={filterTaskTypes.map(t => ({ value: t.id, label: t.name }))}
            value={taskTypeFilter}
            onValueChange={v => { setTaskTypeFilter(v); setPage(1); }}
            placeholder="All Types"
            allowClear
          />
        </div>

        <div className="flex items-center gap-3 ml-auto">
          {hasFilters && <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800 underline">Clear</button>}
          <span className="text-xs text-slate-500 whitespace-nowrap">{meta?.total ?? 0} tasks</span>
        </div>
      </div>

      {/* Table */}
      <div className="erp-card overflow-x-auto">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading...</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No tasks" hint="Create your first task to get started." />
        ) : (
          <table className="erp-table">
            <thead>
              <tr>
                <SortTh label="Task Code" k="task_code" />
                <SortTh label="Title" k="title" />
                <SortTh label="Assignee" k="assigned_to_name" />
                <SortTh label="Status" k="status" />
                <SortTh label="Priority" k="priority_name" />
                <SortTh label="Due Date" k="original_due_date" />
                <th>Source</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(t => (
                <tr key={t.id} data-testid={`task-row-${t.task_code}`}>
                  <td className="font-mono text-xs text-slate-700">{t.task_code}</td>
                  <td className="max-w-[280px]">
                    <div className="font-medium text-slate-900 truncate">{t.title}</div>
                    {t.is_overdue && <div className="text-[10px] text-red-700 uppercase tracking-wider mt-0.5">Overdue</div>}
                  </td>
                  <td className="text-slate-700">{t.assigned_to_name || "—"}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td><PriorityBadge priority={t.priority_name} /></td>
                  <td className="text-xs text-slate-600">{fmtDate(t.original_due_date)}</td>
                  <td className="text-xs"><span className="badge-base bg-slate-50 text-slate-600 border-slate-200">{t.source}</span></td>
                  <td>
                    <button
                      onClick={() => setDetailId(t.id)}
                      className="text-slate-600 hover:text-slate-900 p-1.5 hover:bg-slate-100 rounded-sm"
                      data-testid={`view-task-${t.task_code}`}
                    ><Eye size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {meta?.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="erp-btn-secondary disabled:opacity-50">Prev</button>
          <span className="text-slate-600">Page {page} / {meta.total_pages}</span>
          <button disabled={page >= (meta.total_pages || 1)} onClick={() => setPage(p => p + 1)} className="erp-btn-secondary disabled:opacity-50">Next</button>
        </div>
      )}

      {createOpen && <CreateTaskDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
      {detailId && <TaskDetailDialog taskId={detailId} onClose={() => { setDetailId(null); load(); }} />}
    </div>
  );
}

function CreateTaskDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", assigned_to: "",
    priority_id: "", task_type_id: "", original_due_date: "",
    amount: "",
    approval_required: false, return_confirmation_required: false,
  });
  const [users, setUsers] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/users?limit=200").then(r => setUsers(r.data?.data || []));
    api.get("/masters/priorities").then(r => setPriorities(r.data?.data || []));
    api.get("/masters/task-types").then(r => setTaskTypes(r.data?.data || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.assigned_to) {
      toast.error("Title and assignee are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.original_due_date) {
        payload.original_due_date = new Date(payload.original_due_date).toISOString();
        payload.sla_due_date = payload.original_due_date;
      } else {
        delete payload.original_due_date;
      }
      ["priority_id", "task_type_id"].forEach(k => { if (!payload[k]) delete payload[k]; });
      if (payload.amount === "" || payload.amount === null) {
        delete payload.amount;
      } else {
        payload.amount = Number(payload.amount);
      }
      await api.post("/tasks", payload);
      toast.success("Task created");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="create-task-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Create Task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Title *</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="erp-input mt-1" data-testid="task-title-input" required />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="erp-input mt-1" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Assignee *</label>
              <div className="mt-1" data-testid="task-assignee-select">
                <SearchableSelect
                  options={users.map(u => ({ value: u.id, label: `${u.name} · ${u.email}` }))}
                  value={form.assigned_to}
                  onValueChange={v => setForm({...form, assigned_to: v})}
                  placeholder="Select user"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Priority</label>
              <div className="mt-1">
                <SearchableSelect
                  options={priorities.map(p => ({ value: p.id, label: p.name }))}
                  value={form.priority_id}
                  onValueChange={v => setForm({...form, priority_id: v})}
                  placeholder="Select priority"
                  allowClear
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Task Type</label>
              <div className="mt-1">
                <SearchableSelect
                  options={taskTypes.map(p => ({ value: p.id, label: p.name }))}
                  value={form.task_type_id}
                  onValueChange={v => setForm({...form, task_type_id: v})}
                  placeholder="Select task type"
                  allowClear
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Due Date</label>
              <input type="datetime-local" value={form.original_due_date} onChange={e => setForm({...form, original_due_date: e.target.value})} className="erp-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Amount (optional)</label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} className="erp-input mt-1" placeholder="e.g. 75000 for high-value workflow" data-testid="task-amount-input" />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.approval_required} onChange={e => setForm({...form, approval_required: e.target.checked})} className="rounded-sm" data-testid="approval-required-cb"/>
              <span>Approval Required</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.return_confirmation_required} onChange={e => setForm({...form, return_confirmation_required: e.target.checked})} className="rounded-sm" data-testid="return-conf-cb"/>
              <span>Return Confirmation Required</span>
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="erp-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="erp-btn-primary inline-flex items-center gap-2" data-testid="submit-task-btn">
              {submitting && <Loader2 size={14} className="animate-spin" />} Create Task
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetailDialog({ taskId, onClose }) {
  const { hasPerm, user } = useAuth();
  const [task, setTask] = useState(null);
  const [timeline, setTimeline] = useState({ status_history: [], approval_history: [], return_history: [] });
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState("");

  const load = useCallback(async () => {
    const [t, tl] = await Promise.all([
      api.get(`/tasks/${taskId}`),
      api.get(`/tasks/${taskId}/timeline`),
    ]);
    setTask(t.data?.data || null);
    setTimeline(tl.data?.data || { status_history: [] });
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action, label) => {
    setActionLoading(true);
    try {
      await api.post(`/tasks/${taskId}/${action}`, { remarks });
      toast.success(`${label} done`);
      setRemarks("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setActionLoading(false);
    }
  };

  const isAssignee = task?.assigned_to === user?.id;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog" aria-describedby={undefined}>
        {!task ? (
          <>
            <DialogTitle className="sr-only">Loading task…</DialogTitle>
            <div className="p-8 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading…</div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-slate-500">{task.task_code}</span>
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority_name} />
              </div>
              <DialogTitle className="font-heading text-xl mt-1">{task.title}</DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="details" className="mt-3">
              <TabsList>
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="timeline" data-testid="tab-timeline">Lifecycle Timeline</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <div className="grid grid-cols-2 gap-3 text-sm py-2">
                  <Field label="Assignee" value={task.assigned_to_name} />
                  <Field label="Assigned By" value={task.assigned_by_name} />
                  <Field label="Due Date" value={fmtDate(task.original_due_date)} />
                  <Field label="SLA Due" value={fmtDate(task.sla_due_date)} />
                  <Field label="Approval Required" value={task.approval_required ? "Yes" : "No"} />
                  <Field label="Return Confirmation" value={task.return_confirmation_required ? "Yes" : "No"} />
                  <Field label="Source" value={task.source} />
                  <Field label="Completed" value={fmtDate(task.completed_at)} />
                </div>

                {task.description && (
                  <div className="mt-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Description</div>
                    <div className="text-sm text-slate-700 bg-slate-50 p-3 rounded-sm border border-slate-200">{task.description}</div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 border-t pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Actions</div>
                  <textarea
                    value={remarks} onChange={e => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                    className="erp-input mb-2" rows={2}
                    data-testid="action-remarks"
                  />
                  <div className="flex flex-wrap gap-2">
                    {(isAssignee || hasPerm("edit_task")) && task.status === "Assigned" && (
                      <button onClick={() => doAction("accept", "Accepted")} disabled={actionLoading} className="erp-btn-secondary" data-testid="accept-btn">Accept</button>
                    )}
                    {(isAssignee || hasPerm("edit_task")) && ["Assigned", "Accepted"].includes(task.status) && (
                      <button onClick={() => doAction("start", "Started")} disabled={actionLoading} className="erp-btn-secondary" data-testid="start-btn">Start</button>
                    )}
                    {(isAssignee || hasPerm("edit_task")) && ["Accepted", "In Progress", "Rework Required"].includes(task.status) && (
                      <button onClick={() => doAction("complete", "Completed")} disabled={actionLoading} className="erp-btn-primary" data-testid="complete-btn">Mark Complete</button>
                    )}
                    {hasPerm("approve_task") && task.status === "Awaiting Approval" && (
                      <>
                        <button onClick={() => doAction("approve", "Approved")} disabled={actionLoading} className="erp-btn-primary" data-testid="approve-btn">Approve</button>
                        <button onClick={() => doAction("reject", "Rejected")} disabled={actionLoading} className="erp-btn-danger" data-testid="reject-btn">Reject</button>
                        <button onClick={() => doAction("rework", "Rework")} disabled={actionLoading} className="erp-btn-secondary" data-testid="rework-btn">Rework</button>
                      </>
                    )}
                    {task.status === "Return Confirmation Pending" && (
                      <>
                        <button onClick={() => doAction("return-confirm", "Confirmed")} disabled={actionLoading} className="erp-btn-primary" data-testid="return-confirm-btn">Confirm Received</button>
                        <button onClick={() => doAction("return-reject", "Not Received")} disabled={actionLoading} className="erp-btn-danger">Mark Not Received</button>
                      </>
                    )}
                    {hasPerm("reopen_task") && task.status === "Closed" && (
                      <button onClick={() => doAction("reopen", "Reopened")} disabled={actionLoading} className="erp-btn-secondary">Reopen</button>
                    )}
                    {hasPerm("edit_task") && !["Closed", "Cancelled"].includes(task.status) && (
                      <button onClick={() => doAction("cancel", "Cancelled")} disabled={actionLoading} className="erp-btn-secondary">Cancel</button>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="timeline">
                <div className="pt-3">
                  <div className="relative pl-4 border-l-2 border-slate-200 space-y-4">
                    {timeline.status_history.length === 0 && <div className="text-xs text-slate-500">No events yet</div>}
                    {timeline.status_history.map((s) => (
                      <div key={s.id} className="relative">
                        <div className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-slate-900 border-2 border-white" />
                        <div className="text-sm text-slate-800">
                          <span className="font-medium">{s.old_status || "Created"}</span>
                          <span className="text-slate-400 mx-1">→</span>
                          <span className="font-semibold">{s.new_status}</span>
                        </div>
                        <div className="text-xs text-slate-500">{s.changed_by_name} · {fmtDate(s.changed_at)} · <span className="font-mono">{s.source}</span></div>
                        {s.remarks && <div className="text-xs text-slate-600 mt-0.5">"{s.remarks}"</div>}
                      </div>
                    ))}
                  </div>
                  {timeline.approval_history?.length > 0 && (
                    <div className="mt-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Approval History</div>
                      <div className="space-y-2 text-sm">
                        {timeline.approval_history.map(a => (
                          <div key={a.id} className="text-slate-700 border-l-2 border-indigo-300 pl-3">
                            <span className="font-medium">{a.action.toUpperCase()}</span> by {a.approver_name} · {fmtDate(a.action_at)}
                            {a.remarks && <div className="text-xs text-slate-500">"{a.remarks}"</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {timeline.return_history?.length > 0 && (
                    <div className="mt-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Return Confirmation</div>
                      <div className="space-y-2 text-sm">
                        {timeline.return_history.map(r => (
                          <div key={r.id} className="text-slate-700 border-l-2 border-emerald-300 pl-3">
                            <span className="font-medium">{r.confirmation_status}</span> by {r.confirmed_by_name} · {fmtDate(r.confirmed_at)}
                            {r.remarks && <div className="text-xs text-slate-500">"{r.remarks}"</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-900">{value || "—"}</div>
    </div>
  );
}
